import { ICellRendererAngularComp } from 'ag-grid-angular';
import { GridOptions, GroupCellRenderer, ICellRendererParams, RowDataUpdatedEvent, RowNode } from 'ag-grid-community';
import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { cloneDeep } from 'lodash';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { AccountLanguage, AccountLanguageSkill, CategorySelection, CategorySet, CategorySetCategory, SelectedCategory } from 'src/app/cxone/dashboard/modals/category-selection-modal/category-selection-modal.model';
import { CategorySelectionModalService } from 'src/app/cxone/dashboard/modals/category-selection-modal/category-selection-modal.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { AgGridAngular } from 'ag-grid-angular';

@Component({
    selector: 'cv-category-selection-modal',
    templateUrl: './category-selection-modal.component.html',
    styleUrls: ['./category-selection-modal.component.scss']
})
export class CategorySelectionModalComponent implements OnInit {
    @ViewChild('categorySelectionModalGrid') agGrid: AgGridAngular;
    public selectedCategories: SelectedCategory[];

    //external settings
    public hasAutoSelect = false;
    public manualSelect = true;
    public multiSelect = true;
    public moduleKey: string;

    //omnibar
    public itemCountLabel: string; // # Categories localized
    public searchPlaceholderText = "Search categories"; //localized search
    public filterText: string;
    public selectedLang: AccountLanguage;
    public langOptions: AccountLanguage[] = [];
    public langSkills: AccountLanguageSkill[] = [];

    public noData = false;
    public gridOptions: GridOptions = {
        headerHeight: 0,
        columnDefs: [],
        defaultColDef: { flex: 1, },
        groupDefaultExpanded: 1,
        autoGroupColumnDef: {
            colId: "ag-Grid-AutoColumn",
            headerName: "",
            suppressMenu: true,
            cellRendererParams: {
                checkbox: () => this.multiSelect, //pass custom param in as a function so we can get its updated value
                suppressCount: true,
                innerRenderer: "catRenderer",
                multiSelect: () => this.multiSelect,
            },
        },
        treeData: true,
        rowSelection: (this.multiSelect) ? "multiple" : "single",
        rowMultiSelectWithClick: true,
        suppressRowClickSelection: true,
        suppressNoRowsOverlay: true,
        getDataPath: (data: any) => {
            const path = data.path.split('/');
            path.forEach(e => e = e.trim());
            return path;
        },
        onRowDataUpdated: (params: RowDataUpdatedEvent) => {
            this.itemCountLabel = `${this.categories.length} ${this.localization.get("Categories") ?? "categories"}`;
            if (this.selectedCategories.length > 0) {
                // selected categories are the leaves
                this.selectedCategories.forEach(cat => {
                    params.api.forEachLeafNode(node => {
                        if (node.data.id === cat.id)
                            node.setSelected(true);
                    });
                });

                // select parents based on children. undefined == indeterminate
                params.api.forEachNode(node => {
                    if (node instanceof RowNode) {
                        if (node.hasChildren())
                            node.calculateSelectedFromChildren();
                    }
                });
            }
        },
        onSelectionChanged: (event) => {
            event.api.getModel().forEachNode((node, index) => {
                if (node.isSelected())
                    return;
                const a = event.api.getCellRendererInstances({
                    columns: ["ag-Grid-AutoColumn"],
                    rowNodes: [node]
                });
                if (!!a && a.length > 0) {
                    const radioElement = (a[0] as GroupCellRenderer).getFocusableElement().getElementsByTagName("input")[0];
                    if(radioElement)
                        radioElement.checked = false;
                }
            });
        },
        onRowSelected: (event) => {
            if (this.multiSelect) {
                const selected = event.node.isSelected();
                if (typeof(selected) !== "undefined")
                    event.node.childrenAfterFilter.forEach(c => c.setSelected(selected));
                if (event.node.parent && event.node.parent instanceof RowNode) {
                    event.node.parent.calculateSelectedFromChildren();
                }
            }
        },
        components: { catRenderer: CSMInnerRenderer },
        onGridReady: (event) => {
            this.loadData();
        },
    };
    public categories: CategorySetCategory[] = [];
    public categorySet: CategorySet;
    public langSets: CategorySet[] = [];
    public localization: Map<string, string> = new Map<string, string>();

    public modalTitle = "Select Categories";

    constructor(private service: CategorySelectionModalService, private locSrvc: CVLocalizationService, private ref: DynamicDialogRef,
        private conf: DynamicDialogConfig) {
    }

    async ngOnInit() {
        this.moduleKey = (this.conf.data.moduleKey == null) ? "" : cloneDeep(this.conf.data.moduleKey);
        this.hasAutoSelect = this.conf.data.hasAutoSelect ?? true;
        this.manualSelect = this.conf.data.manualSelect;
        this.multiSelect = this.conf.data.multiSelect ?? true;
        this.selectedCategories = (this.conf.data.selectedCategories == null ||
            !Array.isArray(this.conf.data.selectedCategories)) ? [] : cloneDeep(this.conf.data.selectedCategories);
        this.selectedLang = this.conf.data.selectedLang;

        if (this.manualSelect == null || this.manualSelect === undefined) {
            if (this.hasAutoSelect)
                this.manualSelect = false;
            else
                this.manualSelect = true;
        }

        this.localization = await this.locSrvc.GetSection("Category_Selection_Modal");

        if (this.multiSelect) {
            this.modalTitle = this.localization.get("Select_Categories") ?? "Select Categories";
            this.gridOptions.rowSelection = "multiple";
        }
        else {
            this.modalTitle = this.localization.get("Select_Category") ?? "Select Category";
            this.gridOptions.rowSelection = "single";
        }
        this.searchPlaceholderText = this.localization.get("Search") ?? "Search categories";

        const catSetDTO = await this.service.loadData(this.moduleKey);
        if (catSetDTO != null) {
            this.noData = false;
            this.categorySet = catSetDTO.categorySet;
            this.langSets = catSetDTO.languageSets;
            if (!!catSetDTO.accountLanguages && !!catSetDTO.accountLanguagesSkills) {
                this.langOptions = catSetDTO.accountLanguages;
                this.langSkills = catSetDTO.accountLanguagesSkills;

                if (catSetDTO.accountLanguages.length > 0)
                    if(this.selectedLang == null)
                        this.selectedLang = catSetDTO.accountLanguages[0];
            }
        }
        else
            this.noData = true;

        this.resize(this.manualSelect);
        this.loadData();
    }

    loadData() {
        if (!this?.categorySet?.tree?.categories)
            return;

        this.categories = [];

        const langIndex = [...new Set(this.langSkills.map((ls) => ls.langCode))].findIndex((e) => e === this.selectedLang.abbreviation);
        if (langIndex > -1) {
            this.categories.splice(0);
            this.langSets[langIndex].tree.categories.forEach(e => {
                this.categories.push(e);
            });
        }
        else
            this.categories = this.categorySet.tree.categories;

        if (!!this.agGrid?.api)
            this.agGrid?.api.setGridOption('rowData', this.categories); //load data, otherwise ongridready will handle it
    }

    resize(manualSelect: boolean) {
        const elements = document.getElementsByClassName("cxone-modal-wrapper");
        if (elements.length === 0)
            return;
        const element = elements.item(0) as HTMLElement;
        element.style.height = `${(manualSelect) ? 500 : 232}px`;

        if (manualSelect)
            this.loadData();
    }

    filterChanged(event) {
        this.agGrid?.api.setGridOption('quickFilterText', event);
    }

    save() {
        const nodes = this.agGrid?.api?.getSelectedNodes() ?? null;
        this.selectedCategories.splice(0);

        if (this.manualSelect) {
            if (nodes != null && nodes.length > 0)
                nodes.forEach(node => {
                    if (node instanceof RowNode) {
                        if (!node.hasChildren()) {
                            this.selectedCategories.push({
                                id: node.data?.id,
                                title: node.data?.title
                            });
                        }
                    }
                });
            else
                this.manualSelect = !this.manualSelect;
        }

        const rtrn: CategorySelection = {
            selectedCategories: this.selectedCategories,
            manualSelect: this.manualSelect,
            selectedLang: this.selectedLang,
        };
        this.ref.close(rtrn);
    }

    cancel() {
        this.ref.close(null);
    }
}

@Component({
    selector: 'category-cellrenderer',
    template: `
        <ng-container *ngIf="singleSelect && notGroup" >
            <input #rowRadio id="rowRadio" type="radio" name="option" (click)="onSelect()"/>
        </ng-container>
         <div id="main">
             <div id="title">{{cellValue}}</div>&nbsp;
             <div id="catRules" [ngbPopover]="labels" placement="bottom-left top-left"
                container="body" popoverClass="category-cxone-popover" triggers="mouseenter:mouseleave">{{labels}}</div>
         </div>
   `,
    styles: [`
        #rowRadio{ display: inline-block; position:absolute; left:10px; top:50%; transform: translate(0,-65%);}
        #main{ display:flex; flex-direction:row; overflow:hidden;}
        #title{  }
        #catRules{ margin-left:10px; font-size:10px; color:#707070; overflow:hidden; text-overflow: ellipsis;}
    `]
})
export class CSMInnerRenderer implements ICellRendererAngularComp, AfterViewInit{

    @ViewChild("rowRadio") rowRadio: ElementRef;// HTMLInputElement;

    public cellValue: string;
    public labels: string;

    public singleSelect = false;
    public notGroup = false;

    private params: ICellRendererParams;

    // gets called once before the renderer is used
    agInit(params: ICellRendererParams): void {
        this.params = params;
        this.getValueToDisplay(params);
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            if (this.rowRadio)
                this.rowRadio.nativeElement.checked = (this.singleSelect && this.params.node.isSelected());
        });
    }

    // gets called whenever the cell refreshes
    refresh(params: ICellRendererParams): boolean {
        this.params = params;
        this.getValueToDisplay(params);
        return true;
    }

    getValueToDisplay(params: ICellRendererParams) {
        this.cellValue = params.valueFormatted ? params.valueFormatted : params.value;

        if (params.node.group)
            this.labels = "";
        else {
            this.notGroup = true;
            this.singleSelect = !(params as any).multiSelect();

            //generate tags/labels for row and tooltip
            let tagStr = "";
            // eslint-disable-next-line @typescript-eslint/prefer-for-of
            for (let i = 0; i < params.node.data.categoryRules.length; ++i) {
                if (params.node.data.categoryRules[i]?.all?.tags != null)
                    params.node.data.categoryRules[i].all.tags.forEach(tag => {
                        if (tagStr.length === 0)
                            tagStr = tag.text;
                        else
                            tagStr += ` | ${tag.text}`;
                    });
                if (params.node.data.categoryRules[i]?.any?.tags != null)
                    params.node.data.categoryRules[i].any.tags.forEach(tag => {
                        if (tagStr.length === 0)
                            tagStr = tag.text;
                        else
                            tagStr += ` | ${tag.text}`;
                    });
                if (params.node.data.categoryRules[i]?.none?.tags != null)
                    params.node.data.categoryRules[i].none.tags.forEach(tag => {
                        if (tagStr.length === 0)
                            tagStr = tag.text;
                        else
                            tagStr += ` | ${tag.text}`;
                    });
            }

            this.labels = tagStr;
        }
    }

    onSelect() {
        this.params.node.setSelected(true, true);
    }

    deselect() {
        if (!!this.rowRadio)
            this.rowRadio.nativeElement.checked = false;
    }
}
